import { CheckCircle, Target, MessageCircle, Heart, TrendingUp } from 'lucide-react';

interface Dimension {
  category: string;
  score: number | string;
  justification: string;
  highlights: string[];
  improvements: string[];
  coaching: string[];
  affirmations?: string[];
  keep_doing?: string[];
}

interface DetailedAnalysisProps {
  dimensions: Dimension[];
  isDarkMode: boolean;
  getScoreColor: (score: number | string) => string;
  getScoreBadge: (score: number | string) => string;
}

export default function DetailedAnalysis({ dimensions, isDarkMode, getScoreColor, getScoreBadge }: DetailedAnalysisProps) {
  return (
    <div className="space-y-6">
      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-6`}>
        👤 Detailed Performance Analysis
      </h3>
      {dimensions?.map((dimension, index) => (
        <div 
          key={index} 
          id={`dimension-${index}`}
          className={`rounded-xl p-6 shadow-lg transition-all duration-500 ${
            isDarkMode 
              ? 'bg-[#0a1414] border border-[#1a2424]' 
              : 'bg-white'
          }`}
        >
          <div className="flex justify-between items-center mb-4">
            <h4 className={`text-lg font-semibold ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              {dimension.category}
            </h4>
            <div className="text-right">
              <span className={`text-lg font-bold`} style={{ color: getScoreColor(dimension.score) }}>
                {dimension.score}/10
              </span>
              <div className={`text-sm`} style={{ 
                color: dimension.score === "N/A" ? '#9CA3AF' :
                typeof dimension.score === 'number' && dimension.score >= 8 ? '#1a9f6b' :
                typeof dimension.score === 'number' && dimension.score >= 6 ? '#fbbf24' : '#ef4444'
              }}>
                {getScoreBadge(dimension.score)}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <p className={`text-sm leading-relaxed ${
              isDarkMode ? 'text-gray-300' : 'text-[#373737]'
            }`}>
              {dimension.justification}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <h5 className="text-green-400 font-medium mb-2 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                Highlights
              </h5>
              <div className="space-y-2">
                {dimension.highlights?.map((highlight, idx) => (
                  <div key={idx} className="text-sm">
                    <div className={`leading-relaxed ${
                      isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                    }`}>
                      {highlight}
                    </div>
                  </div>
                )) || <div className="text-gray-400 text-sm">No highlights available</div>}
              </div>
            </div>

            {/* Affirmations Section - only show if affirmations exist */}
            {dimension.affirmations && dimension.affirmations.length > 0 && (
              <div>
                <h5 className="text-purple-400 font-medium mb-2 flex items-center">
                  <Heart className="w-4 h-4 mr-1" />
                  Affirmations
                </h5>
                <div className="space-y-2">
                  {dimension.affirmations.map((affirmation, idx) => (
                    <div key={idx} className="text-sm">
                      <div className={`leading-relaxed ${
                        isDarkMode ? 'text-purple-200' : 'text-purple-700'
                      }`}>
                        {affirmation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Keep Doing Section - only show if keep_doing exists */}
            {dimension.keep_doing && dimension.keep_doing.length > 0 && (
              <div>
                <h5 className="text-cyan-400 font-medium mb-2 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Keep Doing
                </h5>
                <div className="space-y-2">
                  {dimension.keep_doing.map((keepDoing, idx) => (
                    <div key={idx} className="text-sm">
                      <div className={`leading-relaxed ${
                        isDarkMode ? 'text-cyan-200' : 'text-cyan-700'
                      }`}>
                        {keepDoing}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h5 className="text-yellow-400 font-medium mb-2 flex items-center">
                <Target className="w-4 h-4 mr-1" />
                Areas for Improvement
              </h5>
              <div className="space-y-2">
                {dimension.improvements?.map((improvement, idx) => (
                  <div key={idx} className="text-sm">
                    <div className={`leading-relaxed ${
                      isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                    }`}>
                      {improvement}
                    </div>
                  </div>
                )) || <div className="text-gray-400 text-sm">No improvements available</div>}
              </div>
            </div>

            <div>
              <h5 className="text-blue-400 font-medium mb-2 flex items-center">
                <MessageCircle className="w-4 h-4 mr-1" />
                Coaching Tips
              </h5>
              <div className="space-y-2">
                {dimension.coaching?.map((coach, idx) => (
                  <div key={idx} className="text-sm">
                    <div className={`leading-relaxed ${
                      isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                    }`}>
                      {coach}
                    </div>
                  </div>
                )) || <div className="text-gray-400 text-sm">No coaching tips available</div>}
              </div>
            </div>
          </div>
        </div>
      )) || <div className="text-gray-400">No detailed analysis available</div>}
    </div>
  );
}
